import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart, fmtPrice } from "@/lib/cart";
import { Reveal } from "@/components/Reveal";

const inputCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-pool focus:ring-2 focus:ring-pool/30";

export default function CheckoutPage() {
  const { items, subtotal } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", city: "", state: "", postal_code: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, variant: i.variant, quantity: i.quantity })),
        customer: { name: form.name, email: form.email, phone: form.phone },
        fulfillment,
        shipping_address: fulfillment === "shipping" ? { line1: form.line1, city: form.city, state: form.state, postal_code: form.postal_code } : null,
        origin_url: window.location.origin,
      };
      const r = await api.post("/shop/checkout", payload);
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed — please try again");
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="bg-cream min-h-[50vh]" data-testid="checkout-empty">
        <div className="container-site py-24 text-center">
          <p className="text-slate-500 text-sm">Your bag is empty.</p>
          <Link to="/shop" className="mt-4 inline-block text-pool font-semibold text-sm hover:underline" data-testid="checkout-shop-link">← Back to the Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream" data-testid="checkout-page">
      <section className="container-site py-14 sm:py-20 max-w-4xl">
        <button onClick={() => navigate("/cart")} className="inline-flex items-center gap-2 text-slate-400 hover:text-pool text-xs tracking-[0.15em] uppercase font-bold transition-colors mb-8" data-testid="checkout-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Bag
        </button>
        <Reveal>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-ink" data-testid="checkout-headline">
            Check<span className="text-pool">out.</span>
          </h1>
        </Reveal>

        <form onSubmit={submit} className="mt-10 grid lg:grid-cols-[1fr_380px] gap-8 items-start" data-testid="checkout-form">
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-7">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">Your details</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="checkout-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} data-testid="checkout-email" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={`${inputCls} sm:col-span-2`} data-testid="checkout-phone" />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-7">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">Delivery</p>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="checkout-fulfillment">
                <button
                  type="button"
                  onClick={() => setFulfillment("pickup")}
                  data-testid="fulfillment-pickup"
                  className={`flex items-start gap-3 border rounded-xl p-4 text-left transition-colors ${fulfillment === "pickup" ? "border-pool bg-pool-light/30" : "border-slate-200 hover:border-pool/50"}`}
                >
                  <MapPin className="h-5 w-5 text-pool shrink-0 mt-0.5" />
                  <span>
                    <span className="block font-bold text-sm text-ink">Free Café Pickup</span>
                    <span className="block text-xs text-slate-500 mt-1">3877 Holland Road, Suite 509, Virginia Beach</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillment("shipping")}
                  data-testid="fulfillment-shipping"
                  className={`flex items-start gap-3 border rounded-xl p-4 text-left transition-colors ${fulfillment === "shipping" ? "border-pool bg-pool-light/30" : "border-slate-200 hover:border-pool/50"}`}
                >
                  <Truck className="h-5 w-5 text-pool shrink-0 mt-0.5" />
                  <span>
                    <span className="block font-bold text-sm text-ink">Ship It To Me</span>
                    <span className="block text-xs text-slate-500 mt-1">US addresses · calculated at payment</span>
                  </span>
                </button>
              </div>
              {fulfillment === "shipping" && (
                <div className="grid sm:grid-cols-2 gap-4 mt-5" data-testid="checkout-shipping-fields">
                  <input required value={form.line1} onChange={set("line1")} placeholder="Street address" className={`${inputCls} sm:col-span-2`} data-testid="shipping-line1" />
                  <input required value={form.city} onChange={set("city")} placeholder="City" className={inputCls} data-testid="shipping-city" />
                  <div className="grid grid-cols-2 gap-4">
                    <input required value={form.state} onChange={set("state")} placeholder="State" className={inputCls} data-testid="shipping-state" />
                    <input required value={form.postal_code} onChange={set("postal_code")} placeholder="ZIP" className={inputCls} data-testid="shipping-zip" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-7 lg:sticky lg:top-28" data-testid="checkout-summary">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">Order summary</p>
            <div className="space-y-3">
              {items.map((i) => (
                <div key={i.key} className="flex items-center justify-between gap-3 text-sm">
                  <p className="text-slate-600 min-w-0">
                    <span className="font-semibold text-ink">{i.name}</span>
                    {i.variant && <span className="text-slate-400"> · {i.variant}</span>}
                    <span className="text-slate-400"> × {i.quantity}</span>
                  </p>
                  <p className="font-semibold text-ink shrink-0">{fmtPrice(i.unit_price_cents * i.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Subtotal</p>
              <p className="font-display font-black text-xl text-ink" data-testid="checkout-subtotal">{fmtPrice(subtotal)}</p>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Taxes {fulfillment === "shipping" ? "and shipping " : ""}calculated securely at payment.</p>
            <button
              type="submit"
              disabled={busy}
              className="mt-6 w-full inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark disabled:opacity-60 text-white font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
              data-testid="checkout-submit"
            >
              {busy ? "Redirecting to secure payment…" : <>Continue to Payment <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="mt-3 text-[11px] text-slate-400 text-center">Secure payment by Stripe. Test card: 4242 4242 4242 4242.</p>
          </div>
        </form>
      </section>
    </div>
  );
}
